// modules/funding-options.js (ES module)
// Builds funding cards with desirability-colored badges and renders a list from JSON

import { getAssetsBase } from './assets-base.js';

function clampPt(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 25);
}

const PT_STOP = new Set([
  'a', 'an', 'the', 'to', 'for', 'your', 'and', 'or', 'on', 'in', 'at', 'of', 'with',
  'how', 'our', 'is', 'are', 'be', 'by', 'from', 'that', 'this', 'you', 'we', 'it',
]);

function ptSlug(text, maxLen = 16) {
  if (text === undefined || text === null) return '';
  const words = String(text)
    .toLowerCase()
    .replace(/&[^;]+;/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1 && !PT_STOP.has(w));
  return words.join('_').slice(0, maxLen).replace(/_+$/, '');
}

function ptFrom(prefix, text, suffix) {
  const slug = ptSlug(text);
  const parts = [prefix, slug, suffix].filter(Boolean);
  return clampPt(parts.join('_'));
}

// ---------- helpers shared within this module ----------
function normalizeBullets(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) =>
    item && typeof item === 'object'
      ? { title: item.title || '', text: item.text || '' }
      : { title: '', text: String(item || '') }
  );
}

function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
}

function labelize(value) {
  if (!value) return '';
  return toTitleCase(String(value).replace(/-/g, ' '));
}

function buildSection(opts) {
  const $ = window.jQuery;
  const { heading, iconClass, bodyHtml, listItems } = opts;
  const $section = $('<section>', { class: 'funding-card-section' });
  const $icon = $('<span>', {
    class: 'funding-card-section__icon ' + iconClass,
    'aria-hidden': 'true',
  });
  const $body = $('<div>', { class: 'funding-card-section__body' });
  $body.append(
    $('<h4>', {
      class: 'funding-card-section__title',
      text: String(heading || ''),
    })
  );

  if (bodyHtml) {
    $body.append($('<p>', { class: 'funding-card-section__text' }).html(bodyHtml));
  }

  const list = normalizeBullets(listItems);
  if (list.length) {
    const $ul = $('<ul>', { class: 'funding-card-section__list' });
    list.forEach((it) => {
      const text =
        (it.title ? '<span class="lead">' + it.title + '</span> ' : '') +
        (it.text || '');
      $ul.append($('<li>').html(text));
    });
    $body.append($ul);
  }

  return $section.append($icon, $body);
}

// ---------- desirability mapping ----------
function norm(v) {
  return String(v || '').toLowerCase();
}

// 5 = most desirable (green) … 1 = least (coral)
function desireBucket(metric, levelStr) {
  // canonical rank: low … high mapped to 0..4
  const idx = CANON_LEVELS.indexOf(canon(levelStr));
  // guard: unknown values sit in the middle
  const rank = idx === -1 ? 2 : idx;

  // For cost & risk: lower is more desirable (invert)
  // For flex: higher is more desirable (natural)
  if (metric === 'flex') {
    // 0..4 → 1..5
    return rank + 1;
  } else {
    // invert 0..4 so 0→5, 4→1
    return (4 - rank) + 1;
  }
}


function makeBadge(metricLabel, metricKey, value) {
  const $ = window.jQuery;
  const bucket = desireBucket(metricKey, value);
  const text = `${labelize(value)} ${metricLabel}`.toUpperCase();
  return $('<span>', {
    class: `badge badge-${metricKey} desire-${bucket}`,
    'data-level': String(value || ''),
  }).text(text);
}

function makeMetaLine(opt) {
  const $ = window.jQuery;
  const parts = [
    { key: 'cost', label: 'Cost', value: opt.cost_level },
    { key: 'risk', label: 'Risk', value: opt.risk_level },
    { key: 'flex', label: 'Flexibility', value: opt.flexibility_level },
  ];

  const $meta = $('<div>', { class: 'meta', 'aria-label': 'Attributes' });
  parts.forEach((part, i) => {
    if (i > 0) {
      $meta.append($('<span>', { class: 'meta-sep', 'aria-hidden': 'true', text: '·' }));
    }
    $meta.append(makeBadge(part.label, part.key, part.value));
  });
  return $meta;
}

function buildCardBody(opt) {
  const $ = window.jQuery;
  const $body = $('<div>', { class: 'card-body' });

  const desc = Array.isArray(opt.description)
    ? opt.description
    : opt.description
    ? [String(opt.description)]
    : [];
  desc.forEach((p) => $body.append($('<p>', { class: 'funding-card-desc' }).text(p)));

  const $sections = $('<div>', { class: 'funding-card-sections' });

  if (opt.key_consideration) {
    $sections.append(
      buildSection({
        heading: 'Key consideration',
        iconClass: 'funding-key-consideration',
        bodyHtml: opt.key_consideration,
      })
    );
  }

  if (opt.tradeoffs && opt.tradeoffs.advantages) {
    $sections.append(
      buildSection({
        heading: 'Advantages',
        iconClass: 'funding-advantages',
        listItems: opt.tradeoffs.advantages,
      })
    );
  }

  if (opt.tradeoffs && opt.tradeoffs.drawbacks) {
    $sections.append(
      buildSection({
        heading: 'Drawbacks',
        iconClass: 'funding-disadvantages',
        listItems: opt.tradeoffs.drawbacks,
      })
    );
  }

  if (opt.best_for) {
    $sections.append(
      buildSection({
        heading: 'Best for',
        iconClass: 'funding-best-for',
        bodyHtml: opt.best_for,
      })
    );
  }

  if (opt.risk_owner) {
    $sections.append(
      buildSection({
        heading: 'Risk owner',
        iconClass: 'funding-risk-owner',
        bodyHtml: opt.risk_owner,
      })
    );
  }

  $body.append($sections);

  $body.append(
    $('<a>', {
      class: 'funding-card-learn-more chaseanalytics-track-link',
      href: '/sb-health-care-hub/tools/comparing-plan-options.htm',
      'data-pt-name': ptFrom('lnk', 'Learn more about evaluating plan options'),
    })
      .append($('<span>', { text: 'Learn more about evaluating plan options' }))
      .append(
        $('<img>', {
          class: 'funding-card-learn-more__arrow',
          src: getAssetsBase() + 'img/smbhc-hub-funding-cta-arrow-blue.svg',
          alt: '',
          'aria-hidden': 'true',
        })
      )
  );

  if (opt.callout && opt.callout.title && opt.callout.text && opt.callout.link) {
    const $callout = $('<div>', { class: 'col-12 col-md-10 mx-auto mb-0 mt-5 text-center callout' })
      .append('<hr class="my-4" />')
      .append($('<h3>', { class: 'p mb-4', text: opt.callout.title }))
      .append(
        $('<a>', {
          class: 'btn dark-blue mx-auto mt-0 chaseanalytics-track-link',
          href: opt.callout.link,
          text: opt.callout.text,
          'data-pt-name': ptFrom('btn', opt.callout.text),
        })
      );
    $body.append($callout);
  }

  return $body;
}

// ---------- card factory ----------
function buildFundingCard(opt, idx) {
  const $ = window.jQuery;
  const cardId = 'funding-' + (opt.id || 'item-' + idx);
  const collapseId = cardId + '-collapse';
  const altClass = idx % 2 === 0 ? 'funding-card--white' : 'funding-card--grey';

  const $col = $('<div>', { class: 'col-12' });
  const $card = $('<article>', {
    class: 'funding-card card h-100 ' + altClass,
    role: 'article',
    'aria-labelledby': cardId + '-title',
  });

  const $meta = makeMetaLine(opt);
  const $title = $('<h3>', {
    class: 'card-title mt-1',
    id: cardId + '-title',
    text: opt.title || 'Funding option',
  });

  // ===== Desktop header/body (hidden on mobile) =====
  const $headerDesktop = $('<div>', { class: 'card-header d-none d-lg-block' }).append(
    $meta.clone(true),
    $title.clone(true)
  );
  const $bodyDesktop = buildCardBody(opt).addClass('d-none d-lg-block');

  // ===== Mobile collapsible duplicate =====
  const $headerMobileBtn = $('<button>', {
    class: 'card-header w-100 text-start d-lg-none d-flex justify-content-between align-items-end chaseanalytics-track-link',
    type: 'button',
    'data-bs-toggle': 'collapse',
    'data-bs-target': '#' + collapseId,
    'aria-expanded': 'false',
    'aria-controls': collapseId,
    'data-pt-name': ptFrom('btn', opt.title || 'Funding option', 'expand'),
  })
    .append($('<div>').append($meta.clone(true), $title.clone(true)))
    .append($('<span>', { class: 'collapse-arrow ms-2', html: '&#9662;' }));

  const $bodyMobile = $('<div>', {
    id: collapseId,
    class: 'collapse d-lg-none',
  });

  $bodyMobile.on('show.bs.collapse', function () {
    $headerMobileBtn.find('.collapse-arrow').addClass('rotate-180');
  });
  $bodyMobile.on('hide.bs.collapse', function () {
    $headerMobileBtn.find('.collapse-arrow').removeClass('rotate-180');
  });

  const $bodyMobileInner = buildCardBody(opt);
  $bodyMobile.append($bodyMobileInner);

  $card.append($headerDesktop, $bodyDesktop, $headerMobileBtn, $bodyMobile);

  return $col.append($card);
}



// ---------- public API ----------
// ---- filtering + persistence helpers ---------------------------------------
const FILTER_STORAGE_KEY = 'fundingFilters:v2';
const SORT_STORAGE_KEY   = 'fundingSort:v2';

// Default selection: Cost Low + Financial risk Low
const DEFAULT_FILTER_IDS = ['#cost-low', '#risk-low'];

// --- canonical levels & helpers ---------------------------------------------
const CANON_LEVELS = ['low','medium-low','medium','medium-high','high'];

function canon(v) {
  const s = String(v || '').toLowerCase().trim();
  // accept either "low-medium" or "medium-low", normalize to "medium-low"
  if (s === 'low-medium') return 'medium-low';
  return s;
}

// Expand UI chip → included raw levels
function expandChip(val) {
  switch (canon(val)) {
    case 'low':    return ['low', 'medium-low'];
    case 'medium': return ['medium-low', 'medium', 'medium-high'];
    case 'high':   return ['medium-high', 'high'];
    default:       return [];
  }
}

// Build expanded filter sets per group from the panel state
function readFilterStateFromDOM($panel) {
  const $ = window.jQuery;
  const expanded = {};
  $panel.find('.filter-group[data-filter-group]').each(function () {
    const group = $(this).data('filter-group'); // cost | risk | flex
    const chosenChips = $(this)
      .find('input[type="checkbox"]:checked')
      .map(function () { return this.value; })
      .get();

    // union of included levels per selected chip
    const union = new Set();
    chosenChips.forEach(chip => expandChip(chip).forEach(v => union.add(canon(v))));
    expanded[group] = Array.from(union); // [] means "no filter" for that group
  });
  return expanded;
}

// True when the user has selected at least one filter chip in any group
function hasActiveFilters(state) {
  return Object.values(state || {}).some((vals) => (vals || []).length > 0);
}

// AND across groups; OR within a group, using expanded sets
function itemMatchesFilters(item, state) {
  const groups = Object.keys(state || {});
  for (const g of groups) {
    const allowed = (state[g] || []).map(canon);
    if (!allowed.length) continue; // no selection in this group → pass
    const raw =
      g === 'cost' ? item.cost_level :
      g === 'risk' ? item.risk_level :
      g === 'flex' ? item.flexibility_level : '';
    if (!allowed.includes(canon(raw))) return false;
  }
  return true;
}


function saveFilters(state){ try{ localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state)); }catch{} }
function loadFilters(){ try{ return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}'); }catch{ return {}; } }

function saveSort(val){ try{ localStorage.setItem(SORT_STORAGE_KEY, String(val || 'a-z')); }catch{} }
function loadSort(){ try{ return localStorage.getItem(SORT_STORAGE_KEY) || 'a-z'; }catch{ return 'a-z'; } }

// Build/update pills in the filter bar
function updatePillsUI({ $, $bar, state }) {
  const $pills = $bar.find('.filter-bar__pills');
  const $label = $bar.find('.filter-bar__label');
  $pills.empty();

  const pretty = { cost: 'Cost', risk: 'Risk', flex: 'Flexibility' };
  let count = 0;

  Object.entries(state || {}).forEach(([group, vals]) => {
    (vals || []).forEach((v) => {
      count++;
      const text = `${v.replace(/-/g, ' ')} ${pretty[group]}`;
      const $pill = $('<button>', {
        type: 'button',
        class: 'pill chaseanalytics-track-link',
        'data-remove-group': group,
        'data-remove-value': v,
        'aria-label': `Remove ${text}`,
        'data-pt-name': ptFrom('btn', text, 'remove'),
      }).text(text);
      $pills.append($pill);
    });
  });

  $bar.toggleClass('has-selection', count > 0);
  // keep the centered label text; CSS can float it when .has-selection
  $label.text('Filter funding types');
}

// ---- main init function ----------------------------------------------------
export function initFundingOptions({
  dataUrl,
  target      = '#funding-cards',
  sortSelect  = '#sort-select',
  filterPanel = '#funding-filter-panel',
  filterBar   = '.filter-bar'
} = {}) {
  const $ = window.jQuery;

  const $grid   = $(target);
  const $select = $(sortSelect);
  const $panel  = $(filterPanel);
  const $bar    = $(filterBar);

  if (!$grid.length) return;

  const resolvedDataUrl = dataUrl || `${getAssetsBase()}data/sb-healthcare-hub-module2.json`;

  let data     = [];
  let filters  = {};
  let sortMode = 'a-z';

  const comparators = {
    'a-z'         : (a,b) => (a.title||'').localeCompare(b.title||''),
    'z-a'         : (a,b) => (b.title||'').localeCompare(a.title||''),
    'lowest-cost' : (a,b) => desireBucket('cost', b.cost_level) - desireBucket('cost', a.cost_level),
    'highest-cost': (a,b) => desireBucket('cost', a.cost_level) - desireBucket('cost', b.cost_level),
    'lowest-risk' : (a,b) => desireBucket('risk', b.risk_level) - desireBucket('risk', a.risk_level),
    'highest-risk': (a,b) => desireBucket('risk', a.risk_level) - desireBucket('risk', b.risk_level),
    'highest-flex': (a,b) => desireBucket('flex', b.flexibility_level) - desireBucket('flex', a.flexibility_level),
    'lowest-flex' : (a,b) => desireBucket('flex', a.flexibility_level) - desireBucket('flex', b.flexibility_level),
  };

  function render(list, { requireFilters = false } = {}) {
    console.log("Rendering", list.length, "funding options");
    $grid.empty();
  
    // update counter
    const count = list.length;
    const label = count === 1 ? 'funding option result' : 'funding options results';
    $('#funding-options-num').text(`${count} ${label}`);
  
    // no-results message — only when filters are active but nothing matched
    if (!count) {
      if (requireFilters) return;

      const $wrapper = $('<div>', { class: 'col-12' });
      const $alert = $('<div>', {
        class: 'no-results alert alert-info',
        role: 'status',
        'aria-live': 'polite'
      }).text('No results found, please revise filter selections.');

      $wrapper.append($alert);
      $grid.append($wrapper);
      return;
    }

  
    // render cards
    list.forEach((opt, idx) => $grid.append(buildFundingCard(opt, idx)));

    window.GlossaryTooltips.refresh();
    
  }
  

  function apply() {
    const cmp = comparators[sortMode] || comparators['a-z'];
    // Require at least one filter selection before showing any options
    if (!hasActiveFilters(filters)) {
      render([], { requireFilters: true });
      if ($bar.length) updatePillsUI({ $, $bar, state: filters });
      return;
    }
    const filtered = data.filter(item => itemMatchesFilters(item, filters));
    render(filtered.sort(cmp));
    if ($bar.length) updatePillsUI({ $, $bar, state: filters });
  }

  function setDefaultFilters() {
    $panel.find('input[type="checkbox"]').prop('checked', false);
    $panel.find(DEFAULT_FILTER_IDS.join(',')).prop('checked', true);
  }

  function hydrateFilterPanel(state) {
    if (!state || !Object.keys(state).length) return;
    $panel.find('.filter-group[data-filter-group]').each(function () {
      const group = $(this).data('filter-group');
      const chosen = (state[group] || []).map(String);
      $(this).find('input[type="checkbox"]').each(function () {
        const val = (this.value || '').toLowerCase();
        this.checked = chosen.includes(val);
      });
    });
  }

  function bindEvents() {
    // SORT select + floating label in your split-select
    if ($select.length) {
      const $leadLabel = $select.siblings('.split-select__lead').find('.split-select__label');
      const syncLabel = () => $leadLabel.text($select.find('option:selected').text());

      if (!$select.val()) $select.val(sortMode);
      syncLabel();

      $select.off('change.fundingSort').on('change.fundingSort', function () {
        sortMode = this.value || 'a-z';
        saveSort(sortMode);
        syncLabel();
        apply();
      });
    }

    // FILTER checkboxes (instant apply)
    $panel.off('change.fundingFilters').on('change.fundingFilters', 'input[type="checkbox"]', function () {
      filters = readFilterStateFromDOM($panel);
      saveFilters(filters);
      apply();
    });

    // Reset filters → restore defaults (Cost Low + Risk Low)
    $(document).off('click.fundingReset').on('click.fundingReset', '#funding-reset-filters', function () {
      setDefaultFilters();
      filters = readFilterStateFromDOM($panel);
      saveFilters(filters);
      apply();
    });

    // Pill remove (don’t toggle collapse when clicking pill)
    $bar.off('click.fundingPill').on('click.fundingPill', '.pill', function (e) {
      e.stopPropagation();
      const group = $(this).data('remove-group');
      const val   = $(this).data('remove-value');
      $panel
        .find(`.filter-group[data-filter-group="${group}"] input[value="${val}"]`)
        .prop('checked', false)
        .trigger('change'); // triggers apply()
    });
  }

  // Load data + restore state
  $.getJSON(resolvedDataUrl)
    .done(items => {
      if (!Array.isArray(items) || !items.length) return;
      data = items.slice();

      // restore saved filters/sort; fall back to Cost Low + Risk Low defaults
      filters  = loadFilters();
      sortMode = loadSort();

      if (!hasActiveFilters(filters)) {
        setDefaultFilters();
        filters = readFilterStateFromDOM($panel);
        saveFilters(filters);
      } else {
        hydrateFilterPanel(filters);
      }
      if ($select.length) {
        $select.val(sortMode);
        const $leadLabel = $select.siblings('.split-select__lead').find('.split-select__label');
        console.log("$leadLabel is", $leadLabel);
        console.log("$select.find('option:selected').text() is", $select.find('option:selected').text());
        if ($leadLabel.length) $leadLabel.text($select.find('option:selected').text());
      }
      if ($bar.length) updatePillsUI({ $, $bar, state: filters });

      bindEvents();
      apply();

      // optional tooltips
      if (typeof window.bootstrap !== 'undefined') {
        document.querySelectorAll('[data-bs-toggle="tooltip"]')
          .forEach(el => new window.bootstrap.Tooltip(el));
      }
    })
    .fail(xhr => {
      console.warn('FundingOptions: JSON load failed:', xhr?.status, xhr?.statusText);
    });
}
